import React, {useEffect} from 'react';
import {FormProvider, useForm, UseFormProps} from 'react-hook-form';
import {Form} from 'reactstrap';
import FormFields from './FormFields';
import {yupResolver} from '@hookform/resolvers/yup';
import {FormRendererProps} from '@src/@types/components/Form';
import RenderIf from '../RenderIf/RenderIf';
import FormControls from './FormControls';
import {FormFieldDesignContext} from '@src/contexts/FormFieldDesignContext';
import ErrorRepresentation from '../ErrorRepresentation/ErrorRepresentation';
import {IEmptyModel} from '@src/@interfaces/IEmptyModel';
import useFormFieldDesignContextProviderValue from '@src/hooks/useFormFieldDesignContextProviderValue';

function VoidForm(props: { children?: React.ReactNode } & Record<string, unknown>) {
    return <React.Fragment>{props.children}</React.Fragment>;
}

export default function FormRenderer<Model extends IEmptyModel = IEmptyModel>(props: FormRendererProps<Model>) {
    const initialValues = props.initialValues;

    const formProps: UseFormProps = {
        mode: 'onChange',
        defaultValues: initialValues,
        criteriaMode: 'all',
    };
    if (props.yupScheme) {
        formProps.resolver = yupResolver(props.yupScheme);
    }

    const formMethods = useForm(Object.assign({}, formProps, props.formProps ?? {}));
    const onSubmitHandler = formMethods.handleSubmit(props.onSubmitSuccess, props.onSubmitFail);

    const modelJson = JSON.stringify(props.model);
    useEffect(() => {
        formMethods.reset(initialValues, {keepDefaultValues: true});
    }, [modelJson]);// eslint-disable-line react-hooks/exhaustive-deps

    const designContextValue = useFormFieldDesignContextProviderValue({
        contextValue: props.formFieldDesignContextOptions
    });

    const onSubmit = (e: React.SyntheticEvent) => onSubmitHandler(e)
            .then(response => {
                if (props.onSubmitRequestSuccess) {
                    return props.onSubmitRequestSuccess(response, formMethods);
                }

                return response;
            })
            .catch(error => {
                if (props.onSubmitRequestFail) {
                    return props.onSubmitRequestFail(error, formMethods);
                }

                throw error;
            });

    const FieldsComponent = props.fieldsComponent ?? FormFields;
    const FormComponent = props.skipFormComponent ? VoidForm : Form;

    return <FormProvider {...formMethods}>
        <FormFieldDesignContext.Provider value={designContextValue}>
            <FormComponent onSubmit={onSubmit}>
                {props.error ? (
                        <ErrorRepresentation
                                error={props.error as Error}
                                outsideContainer={true}
                                className="mb-3"
                        />
                ) : null}

                <FieldsComponent<Model>
                        config={props.config}
                        context={props.context}
                        model={props.model}
                        modelOptions={props.modelOptions}
                        formName={props.formName}
                        customFields={props.customFields}
                        customTabsContentWrappers={props.customTabsContentWrappers}
                        customTabsControlWrappers={props.customTabsControlWrappers}
                />

                {props.config.controls ? (
                        <FormControls {...props.config} formName={props.formName}/>
                ) : null}

                <RenderIf expression={props.controls}>{props.controls}</RenderIf>
            </FormComponent>
        </FormFieldDesignContext.Provider>
    </FormProvider>;
}
